import React from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError, getToken } from "@/lib/api";
import { formatIDR, formatDate, QTN_STATUS } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, FileText, ClipboardCheck, RotateCcw, Truck, Pencil, Check, Zap } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function QuotationDetailPage() {
  const { id } = useParams();
  const [q, setQ] = React.useState(null);
  const [sjOpen, setSjOpen] = React.useState(false);
  const [sjNumber, setSjNumber] = React.useState("");
  const load = React.useCallback(async () => { const { data } = await api.get(`/quotations/${id}`); setQ(data); }, [id]);
  React.useEffect(() => { load(); }, [load]);

  const setStatus = async (s) => {
    // Extra confirm for destructive cancellation
    if (s === "cancelled") {
      if (!window.confirm("Batalkan quotation ini? Semua unit fisik yang ter-assign akan otomatis dilepas & inventori kembali tersedia. Tindakan ini tidak dapat diurungkan.")) return;
    }
    try {
      const { data } = await api.post(`/quotations/${id}/status`, { status: s });
      if (s === "approved" && Array.isArray(data.auto_assign)) {
        const rep = data.auto_assign;
        const totalAssigned = rep.reduce((a, r) => a + (r.assigned || 0), 0);
        const shortfall = rep.filter((r) => (r.shortfall || 0) > 0);
        if (totalAssigned > 0) {
          toast.success(`Quotation disetujui · ${totalAssigned} unit fisik ditugaskan otomatis`);
        } else {
          toast.success("Status diubah");
        }
        if (shortfall.length) {
          const detail = shortfall.map((s) => `${s.product_name}: kurang ${s.shortfall} unit`).join(", ");
          toast.warning(`Sebagian unit tidak tersedia — ${detail}`, { duration: 6000 });
        }
      } else if (s === "cancelled") {
        const n = data.released_assets || 0;
        toast.success(n > 0 ? `Quotation dibatalkan · ${n} unit fisik dilepas kembali ke inventori` : "Quotation dibatalkan");
      } else {
        toast.success("Status diubah");
      }
      load();
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const rerunAutoAssign = async () => {
    try {
      const { data } = await api.post(`/quotations/${id}/auto-assign`);
      const rep = data.auto_assign || [];
      const totalAssigned = rep.reduce((a, r) => a + (r.assigned || 0), 0);
      if (totalAssigned > 0) toast.success(`${totalAssigned} unit tambahan ditugaskan`);
      else toast.info("Tidak ada unit tambahan yang bisa ditugaskan (mungkin sudah lengkap atau stok habis)");
      const shortfall = rep.filter((r) => (r.shortfall || 0) > 0);
      if (shortfall.length) {
        const detail = shortfall.map((s) => `${s.product_name}: kurang ${s.shortfall}`).join(", ");
        toast.warning(`Kekurangan — ${detail}`, { duration: 6000 });
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openInNewTab = async (path, filenameHint) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = () => openInNewTab(`/quotations/${id}/pdf`);
  const downloadSuratJalan = () => openInNewTab(`/quotations/${id}/surat-jalan/pdf`);
  const downloadChecklistDelivery = () => openInNewTab(`/quotations/${id}/checklist/pdf?checklist_type=delivery`);
  const downloadChecklistReturn = () => openInNewTab(`/quotations/${id}/checklist/pdf?checklist_type=return`);

  const openEditSJ = () => {
    setSjNumber(q.surat_jalan_number || (q.number || "").replace("QTN", "SJ"));
    setSjOpen(true);
  };
  const saveSJ = async () => {
    try {
      await api.patch(`/quotations/${id}`, { surat_jalan_number: sjNumber });
      toast.success("Nomor Surat Jalan disimpan");
      setSjOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!q) return <div className="text-sm text-neutral-500">Memuat…</div>;
  const isApproved = q.status === "approved";
  const currentSJ = q.surat_jalan_number || (q.number || "").replace("QTN", "SJ");

  return (
    <div className="space-y-4" data-testid="quotation-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-neutral-500">Quotation</div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            {q.number} <StatusBadge statusMap={QTN_STATUS} value={q.status} className="ml-2" />
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-quotation-pdf"><Download className="h-4 w-4 mr-1" /> Unduh PDF</Button>
          {["sent", "approved", "rejected", "expired", "cancelled"].map((s) => (
            <Button key={s} size="sm" variant={q.status === s ? "default" : "outline"}
              className={q.status === s ? (s === "cancelled" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700") : (s === "cancelled" ? "border-red-300 text-red-700 hover:bg-red-50" : "")}
              onClick={() => setStatus(s)} data-testid={`qtn-status-${s}`}>{QTN_STATUS[s].label}</Button>
          ))}
        </div>
      </div>

      {isApproved && (
        <div className="card-flat p-4 bg-green-50/50 border-green-200" data-testid="approved-actions-panel">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-sm font-semibold text-green-800 flex items-center gap-2">
                <Check className="h-4 w-4" /> Quotation Disetujui — Dokumen Pengiriman Tersedia
              </div>
              <div className="text-xs text-neutral-600 mt-1 flex items-center gap-2 flex-wrap">
                <span>No. Surat Jalan:</span>
                <span className="font-mono font-semibold text-neutral-800" data-testid="sj-number-display">{currentSJ}</span>
                <button onClick={openEditSJ} className="inline-flex items-center gap-1 text-green-700 hover:underline text-xs" data-testid="edit-sj-number">
                  <Pencil className="h-3 w-3" /> Ubah
                </button>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={rerunAutoAssign} data-testid="btn-auto-assign" title="Tugaskan ulang unit fisik yang tersedia">
                <Zap className="h-4 w-4 mr-1" /> Auto-Assign Aset
              </Button>
              <Button size="sm" onClick={downloadSuratJalan} className="bg-green-600 hover:bg-green-700" data-testid="btn-surat-jalan">
                <Truck className="h-4 w-4 mr-1" /> Surat Jalan
              </Button>
              <Button size="sm" variant="outline" onClick={downloadChecklistDelivery} data-testid="btn-checklist-delivery">
                <ClipboardCheck className="h-4 w-4 mr-1" /> Checklist Pengiriman
              </Button>
              <Button size="sm" variant="outline" onClick={downloadChecklistReturn} data-testid="btn-checklist-return">
                <RotateCcw className="h-4 w-4 mr-1" /> Checklist Pengembalian
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={sjOpen} onOpenChange={setSjOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ubah Nomor Surat Jalan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-neutral-500">Nomor ini akan tampil di Surat Jalan dan Checklist Kondisi. Standar-nya menggantikan awalan QTN dengan SJ, namun bisa Anda sesuaikan.</p>
            <div>
              <Label>Nomor Surat Jalan</Label>
              <Input value={sjNumber} onChange={(e) => setSjNumber(e.target.value)} data-testid="sj-number-input" placeholder="cth: GRP-SJ-202607-0099" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSjOpen(false)}>Batal</Button>
            <Button onClick={saveSJ} className="bg-green-600 hover:bg-green-700" data-testid="save-sj-number">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="card-flat p-6 print:shadow-none max-w-4xl">
        <div className="flex items-start justify-between border-b border-neutral-200 pb-4 mb-4">
          <div>
            <div className="font-bold text-lg" style={{ fontFamily: "Manrope, sans-serif" }}>Grippel Rental</div>
            <div className="text-xs text-neutral-500 mt-1">Jakarta · +62 21 000 0000<br />info@grippelrental.com · www.grippelrental.com</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-neutral-500">Quotation</div>
            <div className="font-bold text-lg">{q.number}</div>
            <div className="text-xs text-neutral-500 mt-1">Tanggal {formatDate(q.issue_date)}</div>
            <div className="text-xs text-neutral-500">Berlaku s/d {formatDate(q.valid_until)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-500">Kepada</div>
            <div className="font-medium">{q.customer?.name}</div>
            <div className="text-xs text-neutral-500">{q.customer?.billing_address}</div>
            <div className="text-xs text-neutral-500">{q.customer?.phone} · {q.customer?.email}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-500">Project</div>
            <div className="font-medium">{q.project_name || "-"}</div>
            <div className="text-xs text-neutral-500">Periode: {formatDate(q.start_date)} – {formatDate(q.end_date)}</div>
          </div>
        </div>
        <table className="w-full data-table mb-4">
          <thead><tr><th className="text-left">Equipment</th><th className="text-right">Qty</th><th className="text-right">Hari</th><th className="text-right">Harga</th><th className="text-right">Subtotal</th></tr></thead>
          <tbody>
            {q.items?.map((it, i) => (
              <tr key={i}>
                <td>{it.product_name || "-"}</td>
                <td className="text-right">{it.qty}</td>
                <td className="text-right">{it.days}</td>
                <td className="text-right">{formatIDR(it.unit_price)}</td>
                <td className="text-right">{formatIDR(it.qty * it.days * it.unit_price - (it.discount || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end">
          <div className="w-80 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span>{formatIDR(q.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">PPN {q.tax_rate}%</span><span>{formatIDR(q.tax)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Deposit</span><span>{formatIDR(q.deposit)}</span></div>
            <div className="flex justify-between font-bold text-base border-t border-green-600 pt-2 mt-1">
              <span>Grand Total</span><span className="text-green-700">{formatIDR(q.total)}</span>
            </div>
          </div>
        </div>
        {q.terms && <div className="mt-6 text-xs text-neutral-600 border-t border-neutral-200 pt-3"><b>Syarat & Ketentuan:</b> {q.terms}</div>}
      </div>
    </div>
  );
}
